import type { HWPXDocument, WriteOptions, ReadOptions } from './types.js';
import { writeHwpx, readHwpx } from './codecs/hwpx/index.js';
import { readHwp5 } from './codecs/hwp5/index.js';
import { detectFormat } from './utils/detect.js';
import { HWPXError } from './errors.js';
import { writePdf as _writePdf, type PdfOptions } from './codecs/pdf/index.js';

/**
 * HWPXDocument를 .hwpx 바이너리(Uint8Array)로 직렬화
 */
export function write(doc: HWPXDocument, opts?: WriteOptions): Uint8Array {
  return writeHwpx(doc, opts);
}

/**
 * .hwpx 또는 .hwp 바이너리(Uint8Array)를 HWPXDocument로 파싱
 * 포맷 자동 감지 (HWPX ZIP / HWP5 OLE)
 */
export function read(data: Uint8Array, opts?: ReadOptions): HWPXDocument {
  const format = detectFormat(data);
  if (format === 'hwp5') {
    return readHwp5(data);
  }
  if (format === 'unknown') {
    throw new HWPXError('Unknown file format. Expected HWPX or HWP5 file.');
  }
  return readHwpx(data, opts);
}

/**
 * HWPXDocument를 PDF 바이너리(Uint8Array)로 변환
 */
export function writePdf(doc: HWPXDocument, opts?: PdfOptions): Uint8Array {
  return _writePdf(doc, opts);
}
export type { PdfOptions } from './codecs/pdf/index.js';

// ─── Re-exports ───

export { HWPXBuilder } from './builder.js';
export type {
  TextStyleOptions,
  StyledSegment,
  PageSettings,
  TableOptions,
  ImageOptions,
  CellMerge,
  ColumnSettings,
  HeaderFooterOptions,
} from './builder.js';

export {
  createDefaultDocument,
  createDefaultHead,
  createDefaultMeta,
  createDefaultCharProperty,
  createDefaultParaProperty,
  createDefaultSectionDef,
  createDefaultBorderFill,
  createDefaultFontFaces,
  createDefaultStyles,
} from './defaults.js';

export * as utils from './utils/index.js';

export { HWPXError, HWPXValidationError } from './errors.js';

export type {
  HWPXDocument,
  DocumentMeta,
  DocumentHead,
  Section,
  SectionDef,
  Paragraph,
  Run,
  TextRun,
  TableRun,
  PictureRun,
  DrawingRun,
  BreakRun,
  CharProperty,
  ParaProperty,
  FontFace,
  Style,
  BorderFill,
  BorderLine,
  FillBrush,
  Table,
  TableRow,
  TableCell,
  Picture,
  DrawingObject,
  BinDataItem,
  WriteOptions,
  ReadOptions,
  PageMargin,
  ColumnDef,
  HeaderFooterDef,
  SubDocument,
  BulletProperty,
  NumberingProperty,
  NumberingLevel,
  CellPadding,
} from './types.js';
